import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { fieldErrors, issuesOf } from '../../api/errors';
import { orgApi, partyApi } from '../../api/endpoints';
import { categoriesQuery, partyQuery, queryKeys } from '../../api/queries';
import type { PartyType } from '../../api/types';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../../ui/Button';
import { RadioCards } from '../../ui/Choice';
import { cx } from '../../ui/cx';
import { FormErrors } from '../../ui/FormErrors';
import { SelectField } from '../../ui/Select';
import { TextField } from '../../ui/TextField';
import { useToast } from '../../ui/Toast';
import { AddressFields, emptyAddress, toAddressInput, type AddressState } from '../address';
import { describeFrom, optionalText, summaryItems } from '../forms';
import styles from '../sales/Sales.module.css';

export interface SavedCustomer {
  id: string;
  name: string;
}

/**
 * The buyer form (C.02 create, M-05 edit), one form for every container (DESIGN_SYSTEM §6a): inline
 * in the editor's buyer step, and in the Today setup dialog. Fields follow the ETA receiver rules:
 * B and F need a name, an ID and the five address fields, which are enforced at issue (DV-17).
 * The form mounts empty (create) or loads the buyer once (edit); its container closes it.
 */
export function CustomerForm({
  orgId,
  partyId,
  onSaved,
  onCancel,
  className,
}: {
  orgId: string;
  /** Edit this buyer; null = create. */
  partyId: string | null;
  onSaved: (party: SavedCustomer) => void;
  onCancel: () => void;
  className?: string;
}) {
  const { t, pick } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const existing = useQuery({ ...partyQuery(orgId, partyId ?? ''), enabled: Boolean(partyId) });
  const categories = useQuery(categoriesQuery(orgId, 'party'));

  const [type, setType] = useState<PartyType>('B');
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [address, setAddress] = useState<AddressState>(emptyAddress());

  // Edit: fill the form once from the stored buyer (a later refetch never overwrites typing).
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    const p = existing.data;
    if (!partyId || !p || loadedFor.current === partyId) return;
    loadedFor.current = partyId;
    setType(p.partyType);
    setName(p.name);
    setTaxId(p.taxId ?? '');
    setEmail(p.email ?? '');
    setPhone(p.phone ?? '');
    setCategoryId(p.categoryId);
    const a = p.addresses[0];
    setAddress(
      a
        ? {
            country: a.country,
            governate: a.governate,
            regionCity: a.regionCity,
            street: a.street,
            buildingNumber: a.buildingNumber,
            postalCode: a.postalCode ?? '',
            floor: a.floor ?? '',
            room: a.room ?? '',
            landmark: a.landmark ?? '',
            additionalInformation: a.additionalInformation ?? '',
          }
        : emptyAddress(p.partyType === 'F' ? null : 'EG'),
    );
  }, [partyId, existing.data]);

  const save = useMutation({
    mutationFn: async () => {
      let category = categoryId;
      if (newCategory.trim()) {
        category = (
          await orgApi.createCategory(orgId, { scope: 'party', nameAr: newCategory.trim() })
        ).id;
      }
      const body = {
        partyType: type,
        name: name.trim(),
        ...(optionalText(taxId) ? { taxId: taxId.trim() } : {}),
        ...(optionalText(email) ? { email: email.trim() } : {}),
        ...(optionalText(phone) ? { phone: phone.trim() } : {}),
        ...(category ? { categoryId: category } : {}),
        // Every buyer type has an address (country, city, street…), as in the observed quick
        // create; the server reports each missing field.
        address: toAddressInput(address),
      };
      if (partyId) {
        const version = existing.data?.version ?? 1;
        await partyApi.update(orgId, partyId, { version, ...body });
        return { id: partyId, name: body.name };
      }
      const created = await partyApi.create(orgId, {
        ...body,
        isCustomer: true,
        isSupplier: false,
      });
      return { id: created.id, name: body.name };
    },
    onSuccess: async (party) => {
      toast.success(partyId ? t.parties.updated : t.parties.created);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'parties'] });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'categories'] });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales'] });
      onSaved(party);
    },
  });

  const issues = issuesOf(save.error);
  const errors = fieldErrors(t, issues);
  const taxLabel =
    type === 'B' ? t.parties.taxIdB : type === 'P' ? t.parties.taxIdP : t.parties.taxIdF;
  const labels: Record<string, string> = {
    name: t.parties.name,
    taxId: taxLabel,
    email: t.parties.email,
    phone: t.parties.phone,
    'address.country': t.address.country,
    'address.governate': t.address.governate,
    'address.regionCity': t.address.regionCity,
    'address.street': t.address.street,
    'address.buildingNumber': t.address.buildingNumber,
  };

  return (
    <form
      className={cx(styles.formStack, className)}
      data-testid="customer-dialog"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {save.isError ? (
        <FormErrors
          items={summaryItems(
            t,
            issues.length ? issues : [{ path: '(root)', issue: 'unknown' }],
            describeFrom(labels),
          )}
        />
      ) : null}
      <RadioCards<PartyType>
        label={t.parties.type}
        fieldName="partyType"
        value={type}
        onChange={(v) => {
          setType(v);
          if (v === 'F' && address.country === 'EG') setAddress({ ...address, country: null });
          if (v !== 'F' && !address.country) setAddress({ ...address, country: 'EG' });
        }}
        options={(['B', 'P', 'F'] as const).map((v) => ({
          value: v,
          title: t.parties.types[v],
          description: t.parties.typeHints[v],
        }))}
      />
      <div className={styles.fieldGrid}>
        <TextField
          label={t.parties.name}
          fieldName="name"
          value={name}
          onChange={setName}
          isRequired
          errorMessage={errors.name}
        />
        <TextField
          label={taxLabel}
          fieldName="taxId"
          value={taxId}
          onChange={setTaxId}
          dir="ltr"
          isOptional
          errorMessage={errors.taxId}
        />
        <TextField
          label={t.parties.email}
          fieldName="email"
          value={email}
          onChange={setEmail}
          dir="ltr"
          isOptional
          errorMessage={errors.email}
        />
        <TextField
          label={t.parties.phone}
          fieldName="phone"
          value={phone}
          onChange={setPhone}
          dir="ltr"
          isOptional
          placeholder="+20"
          errorMessage={errors.phone}
        />
        <SelectField
          label={t.parties.category}
          fieldName="categoryId"
          value={categoryId ?? '__none'}
          onChange={(v) => setCategoryId(v && v !== '__none' ? v : null)}
          options={[
            { id: '__none', label: t.parties.noCategory },
            ...(categories.data ?? []).map((c) => ({
              id: c.id,
              label: pick(c.nameAr, c.nameEn ?? c.nameAr),
            })),
          ]}
        />
        <TextField
          label={t.parties.newCategory}
          fieldName="newCategory"
          value={newCategory}
          onChange={setNewCategory}
          isOptional
        />
      </div>
      <AddressFields
        value={address}
        onChange={setAddress}
        errors={errors}
        prefix="address."
        isRequired
        {...(type === 'F' ? { excludeCountry: 'EG' } : {})}
      />
      <div className={styles.dialogFooter}>
        <Button variant="secondary" onPress={onCancel}>
          {t.common.cancel}
        </Button>
        <Button variant="primary" type="submit" isPending={save.isPending} testId="customer-save">
          {partyId ? t.parties.save : t.parties.create}
        </Button>
      </div>
    </form>
  );
}
